'use client';

import { createClient } from '@/lib/supabase/client';

export interface PriceAlert {
  id: string;
  userId: string;
  productId: string;
  productName: string;
  productNameTa: string;
  condition: 'below' | 'above';
  threshold: number;
  isActive: boolean;
  lastTriggeredAt: string | null;
  createdAt: string;
}

function toAlert(row: any): PriceAlert {
  return {
    id: row.id,
    userId: row.user_id,
    productId: row.product_id,
    productName: row.product_name,
    productNameTa: row.product_name_ta,
    condition: row.condition,
    threshold: Number(row.threshold),
    isActive: row.is_active,
    lastTriggeredAt: row.last_triggered_at,
    createdAt: row.created_at,
  };
}

export async function getUserAlerts(userId: string): Promise<PriceAlert[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('price_alerts')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Failed to fetch alerts:', error.message);
    return [];
  }
  return (data || []).map(toAlert);
}

export async function createAlert(
  userId: string,
  alert: Omit<PriceAlert, 'id' | 'userId' | 'lastTriggeredAt' | 'createdAt'>
): Promise<PriceAlert | null> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('price_alerts')
    .insert({
      user_id: userId,
      product_id: alert.productId,
      product_name: alert.productName,
      product_name_ta: alert.productNameTa,
      condition: alert.condition,
      threshold: alert.threshold,
      is_active: alert.isActive,
    })
    .select()
    .single();

  if (error) {
    console.error('Failed to create alert:', error.message);
    return null;
  }
  return toAlert(data);
}

export async function updateAlert(
  alertId: string,
  updates: Partial<Pick<PriceAlert, 'condition' | 'threshold' | 'isActive'>>
): Promise<void> {
  const supabase = createClient();
  const dbUpdates: any = { updated_at: new Date().toISOString() };
  if (updates.condition !== undefined) dbUpdates.condition = updates.condition;
  if (updates.threshold !== undefined) dbUpdates.threshold = updates.threshold;
  if (updates.isActive !== undefined) dbUpdates.is_active = updates.isActive;

  const { error } = await supabase.from('price_alerts').update(dbUpdates).eq('id', alertId);
  if (error) console.error('Failed to update alert:', error.message);
}

export async function deleteAlert(alertId: string): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase.from('price_alerts').delete().eq('id', alertId);
  if (error) console.error('Failed to delete alert:', error.message);
}
